import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { getRandomProfileColor } from '../utils/colorUtils';
import { masterPlayersService } from '../services/supabaseService';
import { useAuth } from './AuthContext';

const MasterPlayerContext = createContext();

const masterPlayerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MASTER_PLAYERS':
      return {
        ...state,
        masterPlayers: action.payload
      };
    case 'ADD_MASTER_PLAYER':
      const newMasterPlayer = {
        id: action.payload.id || Date.now().toString(),
        name: action.payload.name,
        profileColor: action.payload.profileColor || getRandomProfileColor(),
        email: action.payload.email || '',
        phone: action.payload.phone || '',
        notes: action.payload.notes || '',
        createdAt: action.payload.createdAt || new Date().toISOString(),
        updatedAt: action.payload.updatedAt || new Date().toISOString()
      };
      return {
        ...state,
        masterPlayers: [...state.masterPlayers, newMasterPlayer]
      };
    case 'EDIT_MASTER_PLAYER':
      return {
        ...state,
        masterPlayers: state.masterPlayers.map(player =>
          player.id === action.payload.id
            ? { ...player, ...action.payload.updates, updatedAt: new Date().toISOString() }
            : player
        )
      };
    case 'DELETE_MASTER_PLAYER':
      return {
        ...state,
        masterPlayers: state.masterPlayers.filter(player => player.id !== action.payload.id)
      };
    case 'CLEAR_ALL_MASTER_PLAYERS':
      return {
        ...state,
        masterPlayers: []
      };
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.payload
      };
    default:
      return state;
  }
};

const initialState = {
  masterPlayers: [],
  searchTerm: ''
};

import { isSupabaseConfigured } from '../lib/supabase';

export const MasterPlayerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(masterPlayerReducer, initialState);
  const [storedMasterPlayers, setStoredMasterPlayers] = useLocalStorage('masterPlayerList', []);
  const [useSupabase, setUseSupabase] = useState(isSupabaseConfigured());
  const { user, isLoading: authLoading } = useAuth();

  // Load master players on mount and when user/auth state changes
  useEffect(() => {
    // Don't load master players if auth is still loading
    if (authLoading) {
      return;
    }

    const loadMasterPlayers = async () => {
      // If user is logged out, clear master players
      if (!user) {
        dispatch({ type: 'SET_MASTER_PLAYERS', payload: [] });
        return;
      }

      if (useSupabase) {
        try {
          const players = await masterPlayersService.getAll();
          dispatch({ type: 'SET_MASTER_PLAYERS', payload: players });
        } catch (error) {
          console.error('Error loading master players from Supabase:', error);
          setUseSupabase(false);
          dispatch({ type: 'SET_MASTER_PLAYERS', payload: storedMasterPlayers });
        }
      } else {
        dispatch({ type: 'SET_MASTER_PLAYERS', payload: storedMasterPlayers });
      }
    };

    loadMasterPlayers();
  }, [user, authLoading, useSupabase, storedMasterPlayers]);

  // Sync to localStorage if not using Supabase
  useEffect(() => {
    if (!useSupabase && state.masterPlayers.length >= 0) {
      setStoredMasterPlayers(state.masterPlayers);
    }
  }, [state.masterPlayers, useSupabase, setStoredMasterPlayers]);

  const addMasterPlayer = async (playerData) => {
    const isDuplicate = state.masterPlayers.some(player =>
      player.name.toLowerCase() === playerData.name.trim().toLowerCase()
    );
    
    if (isDuplicate) {
      throw new Error('A player with this name already exists in the master list');
    }
    
    const playerWithDefaults = {
      ...playerData,
      profileColor: playerData.profileColor || getRandomProfileColor(),
    };

    if (useSupabase) {
      try {
        const newPlayer = await masterPlayersService.create(playerWithDefaults);
        dispatch({ type: 'ADD_MASTER_PLAYER', payload: newPlayer });
      } catch (error) {
        console.error('Error creating master player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ADD_MASTER_PLAYER', payload: playerWithDefaults });
    }
  };

  const editMasterPlayer = async (playerId, updates) => {
    if (updates.name) {
      const trimmedName = updates.name.trim();
      if (!trimmedName) {
        throw new Error('Player name cannot be empty');
      }
      
      const isDuplicate = state.masterPlayers.some(player =>
        player.id !== playerId &&
        player.name.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (isDuplicate) {
        throw new Error('A player with this name already exists in the master list');
      }
      
      updates.name = trimmedName;
    }
    
    if (useSupabase) {
      try {
        const updatedPlayer = await masterPlayersService.update(playerId, updates);
        dispatch({ type: 'EDIT_MASTER_PLAYER', payload: { id: playerId, updates: updatedPlayer } });
      } catch (error) {
        console.error('Error updating master player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'EDIT_MASTER_PLAYER', payload: { id: playerId, updates } });
    }
  };

  const deleteMasterPlayer = async (playerId) => {
    if (useSupabase) {
      try {
        await masterPlayersService.delete(playerId);
        dispatch({ type: 'DELETE_MASTER_PLAYER', payload: { id: playerId } });
      } catch (error) {
        console.error('Error deleting master player:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'DELETE_MASTER_PLAYER', payload: { id: playerId } });
    }
  };

  const clearAllMasterPlayers = async () => {
    if (useSupabase) {
      try {
        await masterPlayersService.clearAll();
        dispatch({ type: 'CLEAR_ALL_MASTER_PLAYERS' });
      } catch (error) {
        console.error('Error clearing master players:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'CLEAR_ALL_MASTER_PLAYERS' });
      setStoredMasterPlayers([]);
    }
  };

  const setSearchTerm = (term) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: term });
  };

  const getFilteredMasterPlayers = () => {
    if (!state.searchTerm) return state.masterPlayers;
    return state.masterPlayers.filter(player =>
      player.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      player.email.toLowerCase().includes(state.searchTerm.toLowerCase())
    );
  };

  const getMasterPlayerById = (id) => {
    return state.masterPlayers.find(player => player.id === id);
  };

  const value = {
    ...state,
    addMasterPlayer,
    editMasterPlayer,
    deleteMasterPlayer,
    clearAllMasterPlayers,
    setSearchTerm,
    getFilteredMasterPlayers,
    getMasterPlayerById
  };

  return (
    <MasterPlayerContext.Provider value={value}>
      {children}
    </MasterPlayerContext.Provider>
  );
};

export const useMasterPlayer = () => {
  const context = useContext(MasterPlayerContext);
  if (!context) {
    throw new Error('useMasterPlayer must be used within a MasterPlayerProvider');
  }
  return context;
};

export default MasterPlayerContext;
